import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  Home, BookOpen, RefreshCw, Mic, TrendingUp, LayoutDashboard,
  Users, Baby, User, Settings, LogOut, Menu, X, Flame, Star, Languages,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/accueil", label: "Accueil", icon: Home, end: true },
  { to: "/lecture", label: "Apprendre à lire", icon: Languages },
  { to: "/apprendre", label: "Apprendre", icon: BookOpen },
  { to: "/reviser", label: "Réviser", icon: RefreshCw },
  { to: "/reciter", label: "Réciter", icon: Mic },
  { to: "/progression", label: "Progression", icon: TrendingUp },
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/prof", label: "Espace Prof", icon: Users, roles: ["teacher", "admin"] },
  { to: "/parent", label: "Espace Parent", icon: Baby, roles: ["parent", "admin"] },
];

const MOBILE_NAV = [
  { to: "/accueil", label: "Accueil", icon: Home, end: true },
  { to: "/lecture", label: "Lire", icon: Languages },
  { to: "/reciter", label: "Réciter", icon: Mic, center: true },
  { to: "/apprendre", label: "Apprendre", icon: BookOpen },
  { to: "/reviser", label: "Réviser", icon: RefreshCw },
];

const Avatar = ({ user, className }) => {
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() || "U";
  if (user?.avatar_url) {
    return <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" className={cn("object-cover", className)} />;
  }
  return (
    <div className={cn("bg-secondary/15 text-secondary font-extrabold flex items-center justify-center", className)}>
      {initials}
    </div>
  );
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen islamic-bg">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-100 p-5 z-40">
        <Logo className="mb-8 px-2" />
        <nav className="flex-1 space-y-1">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-slate-500 hover:bg-slate-50",
              )}
            >
              <n.icon className="w-5 h-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-3 space-y-1">
          <NavLink to="/profil" className={({ isActive }) => cn("flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-colors", isActive ? "bg-primary/10 text-primary" : "text-slate-500 hover:bg-slate-50")} data-testid="nav-profil">
            <User className="w-5 h-5" /> Profil
          </NavLink>
          <NavLink to="/parametres" className={({ isActive }) => cn("flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-colors", isActive ? "bg-primary/10 text-primary" : "text-slate-500 hover:bg-slate-50")} data-testid="nav-parametres">
            <Settings className="w-5 h-5" /> Paramètres
          </NavLink>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm text-red-500 hover:bg-red-50 transition-colors">
            <LogOut className="w-5 h-5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
          <div className="lg:hidden"><Logo withText={false} size="sm" /></div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-accent/10 text-yellow-600 px-3 py-1.5 rounded-full font-extrabold text-sm" data-testid="header-streak">
              <Flame className="w-4 h-4" /> {user?.streak ?? 0}
            </div>
            <div className="flex items-center gap-1.5 bg-secondary/10 text-secondary px-3 py-1.5 rounded-full font-extrabold text-sm" data-testid="header-stars">
              <Star className="w-4 h-4 fill-current" /> {user?.stars ?? 0}
            </div>
            <NavLink to="/profil" data-testid="header-avatar">
              <Avatar user={user} className="w-10 h-10 rounded-full" />
            </NavLink>
            <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="menu-open">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 pb-28 lg:pb-10 max-w-6xl">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-100 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] flex justify-around items-end">
        {MOBILE_NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`bottomnav-${n.to === "/" ? "home" : n.to.slice(1)}`}
            className={({ isActive }) => cn("flex flex-col items-center gap-1 text-[10px] font-bold flex-1", isActive ? "text-primary" : "text-slate-400")}
          >
            {n.center ? (
              <div className="w-12 h-12 -mt-6 rounded-2xl bg-primary text-white flex items-center justify-center shadow-[0_4px_0_#5a8ce0]">
                <n.icon className="w-6 h-6" />
              </div>
            ) : (
              <n.icon className="w-5 h-5" />
            )}
            {n.label}
          </NavLink>
        ))}
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 inset-y-0 w-72 bg-white p-5 flex flex-col animate-float-up">
            <div className="flex items-center justify-between mb-6">
              <Logo size="sm" />
              <button onClick={() => setOpen(false)} data-testid="menu-close"><X className="w-6 h-6 text-slate-500" /></button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto">
              {links.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  className={({ isActive }) => cn("flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm", isActive ? "bg-primary/10 text-primary" : "text-slate-500")}>
                  <n.icon className="w-5 h-5" /> {n.label}
                </NavLink>
              ))}
              <NavLink to="/profil" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm text-slate-500"><User className="w-5 h-5" /> Profil</NavLink>
              <NavLink to="/parametres" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm text-slate-500"><Settings className="w-5 h-5" /> Paramètres</NavLink>
            </nav>
            <button onClick={doLogout} className="flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm text-red-500">
              <LogOut className="w-5 h-5" /> Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
